import {
  DonationFormConsumer,
  TermsAndConditions,
  useDonationFormContext,
} from '@web/components/page-components/DonationForm'
import { QUICK_PAY_PAYMENT_METHOD_TYPES, usePaymentStore } from 'apps/fe-donation/libs/hooks/usePaymentStore'
import StripeWrapper from '../DonationForm/StripeWrapper'
import DonationCheckoutForm from '../DonationForm/DonationCheckoutForm'
import DonationFormFinalStep from '../DonationForm/DonationFormFinalStep'
import DonationFieldsForm from '../DonationForm/DonationFieldsForm'
import { useVirtualGiftSection } from '@web/components/page-components/VirtualGiftSection/VirtualGiftSectionProvider'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import React, { useEffect } from 'react'
import { useHttpClient } from 'apps/fe-donation/libs/hooks/useHttpClient'
import PaymentButtons from '../DonationForm/PaymentButtons'
import { VIRTUAL_GIFT_METADATA_KEY } from './utils'
import VirtualGiftMatchedGiving from './VirtualGiftMatchedGiving'
import { useErrorLogging } from 'apps/fe-donation/libs/hooks/useErrorLogging'
import DonationFormErrorStep from '../DonationForm/DonationFormErrorStep'

const VirtualGiftPaymentForm = () => {
  const paymentMethodType = usePaymentStore((state) => state.paymentMethodType)
  const donationFormSettings = useDonationFormContext((state) => state.settings)
  const scrollIntoView = useDonationFormContext((state) => state.actions.scrollIntoView)
  const paymentTransactionId = usePaymentStore((state) => state.paymentTransactionId)
  const { items, settings, updateItemUrl } = useVirtualGiftSection()
  const httpClient = useHttpClient({ isRelativeUrl: true })
  const { logError } = useErrorLogging()

  useEffect(() => {
    scrollIntoView()
  }, [])

  const isQuickPay = paymentMethodType && QUICK_PAY_PAYMENT_METHOD_TYPES.includes(paymentMethodType)

  const onFinalise = async () => {
    // we process the image upload here
    try {
      const pageState = usePageStore.getState()
      const { assets } = await httpClient<{ assets: { url: string }[] }>('/api/virtual-gift/assets', {
        method: 'PUT',
        body: JSON.stringify({
          items,
          campaignId: pageState.campaignId,
          charityId: pageState.charityId,
          paymentTransactionId,
        }),
      })

      const virtualGiftMetadata = {
        options: settings.styles?.options,
        items: items.map((item, index) => ({ ...item, url: assets[index].url, imageUrl: assets[index].url })),
      }
      usePageStore.setState({
        virtualGift: virtualGiftMetadata,
      })

      window.localStorage.setItem(VIRTUAL_GIFT_METADATA_KEY, JSON.stringify(virtualGiftMetadata))
    } catch (e) {
      console.error('Error uploading virtual gift assets', e)
      logError('Error uploading virtual gift assets')
      // swallo
    }
  }
  const activeMatchedGiving = usePageStore((state) => state.activeMatchedGiving)

  return (
    <DonationFormConsumer
      donationFormProps={donationFormSettings}
      steps={[
        <React.Fragment key="fields-form">
          {activeMatchedGiving && <VirtualGiftMatchedGiving />}
          <DonationFieldsForm actionSlot={<PaymentButtons />} />
          <TermsAndConditions legalUrl={donationFormSettings?.legalUrl} />
        </React.Fragment>,
        ...(isQuickPay
          ? [
              <DonationFormFinalStep
                key="final-step"
                {...donationFormSettings}
                onFinalise={onFinalise}
                // force this to skip the custom field check
                styles={{ ...donationFormSettings.styles, steps: 3 }}
              />,
            ]
          : [
              <StripeWrapper key="stripe-wrapper" paymentMethodType={paymentMethodType!}>
                <DonationCheckoutForm {...donationFormSettings} paymentMethodType={paymentMethodType!} />
              </StripeWrapper>,
              <DonationFormFinalStep key="final-step" {...donationFormSettings} onFinalise={onFinalise} />,
            ]),
        <DonationFormErrorStep key="error-step" {...donationFormSettings} />,
      ]}
    />
  )
}

export default VirtualGiftPaymentForm
